// Block M2 (UI-16b): dragging a task card between the Team board's columns, with a mouse, a pen or a
// finger. Pointer events throughout.
//
//   Mouse and pen: the drag starts once the pointer has moved 6px with the button held, so a click
//     still opens the task.
//   Touch: a LONG PRESS (400ms held still) starts it, so a finger that moves first is scrolling the
//     page or the board, as before. Once a drag has started, the board's own non-passive touchmove
//     listener stops the page scrolling under the finger.
//   Escape, or the browser cancelling the pointer, drops nothing.
//
// The drop calls onDrop(item, toColumn); the caller sends it through the SAME update as the
// "Move to…" menu (MoveToSheet), which stays as the tap alternative and the keyboard route.
// A column is any element with data-drop-column="<key>"; near the board's sides it scrolls sideways
// (on a phone the columns sit side by side and only one fits), and near the window's top and bottom
// the page scrolls.
import { useEffect, useRef, useState } from 'react';

export const LONG_PRESS_MS = 400;
const MOUSE_SLOP = 6;
const TOUCH_SLOP = 8;
const EDGE = 48;
const STEP = 14;

// The column under a point, by its data-drop-column. The dragged card's ghost is pointer-events: none.
// A finger held at the screen's edge (to scroll the board) sits in the board's side gutter, and a
// short column ends above the finger: so a point just beside or below a column counts as that column.
const NEAR_X = 24;
const NEAR_BELOW = 160;
export function columnAt(x, y) {
  if (typeof document === 'undefined' || typeof document.elementFromPoint !== 'function') return null;
  const el = document.elementFromPoint(x, y);
  const hit = el?.closest?.('[data-drop-column]')?.getAttribute('data-drop-column');
  if (hit) return hit;
  const near = [...document.querySelectorAll('[data-drop-column]')].find((c) => {
    const r = c.getBoundingClientRect();
    return r.width > 0 && x >= r.left - NEAR_X && x <= r.right + NEAR_X && y >= r.top && y <= r.bottom + NEAR_BELOW
      && x >= 0 && x <= window.innerWidth;
  });
  return near ? near.getAttribute('data-drop-column') : null;
}

export default function useBoardDrag({ onDrop, boardRef }) {
  const [drag, setDrag] = useState(null);   // what is shown: { item, from, title, x, y, over }
  const g = useRef(null);                   // the gesture under way, if any
  const off = useRef(null);                 // removes the gesture's window listeners
  const raf = useRef(0);
  const onDropRef = useRef(onDrop);
  onDropRef.current = onDrop;

  function finish(drop) {
    const cur = g.current;
    g.current = null;
    if (off.current) { off.current(); off.current = null; }
    if (raf.current) { cancelAnimationFrame(raf.current); raf.current = 0; }
    if (cur?.timer) clearTimeout(cur.timer);
    setDrag(null);
    if (!cur?.active) return;
    // The click a drag ends with must not open the task it was dropped from.
    const swallow = (e) => { e.stopPropagation(); e.preventDefault(); };
    window.addEventListener('click', swallow, { capture: true, once: true });
    setTimeout(() => window.removeEventListener('click', swallow, { capture: true }), 0);
    if (drop && cur.over && cur.over !== cur.from) onDropRef.current?.(cur.item, cur.over);
  }

  function track(cur) {
    const over = columnAt(cur.x, cur.y);
    cur.over = over;
    setDrag({ item: cur.item, from: cur.from, title: cur.title, x: cur.x, y: cur.y, over });
  }

  function edgeScroll() {
    const cur = g.current;
    if (!cur?.active) { raf.current = 0; return; }
    const board = boardRef?.current;
    let moved = false;
    if (board) {
      const r = board.getBoundingClientRect();
      if (cur.x < r.left + EDGE && board.scrollLeft > 0) { board.scrollLeft -= STEP; moved = true; }
      else if (cur.x > r.right - EDGE && board.scrollLeft + board.clientWidth < board.scrollWidth) { board.scrollLeft += STEP; moved = true; }
    }
    if (cur.y < EDGE) { window.scrollBy(0, -STEP); moved = true; }
    else if (cur.y > window.innerHeight - EDGE) { window.scrollBy(0, STEP); moved = true; }
    if (moved) track(cur);
    raf.current = requestAnimationFrame(edgeScroll);
  }

  function activate() {
    const cur = g.current;
    if (!cur || cur.active) return;
    cur.active = true;
    track(cur);
    if (typeof requestAnimationFrame === 'function') raf.current = requestAnimationFrame(edgeScroll);
  }

  function begin(e, item, from, title) {
    const cur = { item, from, title, x0: e.clientX, y0: e.clientY, x: e.clientX, y: e.clientY, type: e.pointerType || 'mouse', pointerId: e.pointerId, active: false, over: null, timer: null };
    g.current = cur;
    const mine = (ev) => cur.pointerId == null || ev.pointerId == null || ev.pointerId === cur.pointerId;
    const move = (ev) => {
      if (g.current !== cur || !mine(ev)) return;
      cur.x = ev.clientX; cur.y = ev.clientY;
      if (cur.active) { track(cur); return; }
      const dist = Math.hypot(cur.x - cur.x0, cur.y - cur.y0);
      if (cur.type === 'touch') { if (dist > TOUCH_SLOP) finish(false); return; }   // a scroll, not a drag
      if (dist > MOUSE_SLOP) activate();
    };
    const up = (ev) => { if (mine(ev)) finish(true); };
    const cancel = (ev) => { if (mine(ev)) finish(false); };
    const key = (ev) => { if (ev.key === 'Escape') finish(false); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', cancel);
    window.addEventListener('keydown', key);
    off.current = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
      window.removeEventListener('keydown', key);
    };
    if (cur.type === 'touch') cur.timer = setTimeout(activate, LONG_PRESS_MS);
  }

  // Once a touch drag is under way the page must not scroll under the finger. The listener is on the
  // board from the start and non-passive, so the browser asks it before scrolling.
  useEffect(() => {
    const board = boardRef?.current;
    if (!board) return undefined;
    const block = (e) => { if (g.current?.active) e.preventDefault(); };
    board.addEventListener('touchmove', block, { passive: false });
    return () => board.removeEventListener('touchmove', block);
  }, [boardRef]);

  // Leaving the page mid-drag drops nothing and leaves no listener behind.
  useEffect(() => () => finish(false), []);   // eslint-disable-line react-hooks/exhaustive-deps

  // Spread on a card the person may move: cardProps(item, fromColumn, title).
  function cardProps(item, from, title) {
    return {
      onPointerDown: (e) => {
        if (g.current) return;
        if ((e.pointerType || 'mouse') === 'mouse' && e.button !== 0) return;
        begin(e, item, from, title);
      },
      // A long press would otherwise open the phone's own menu over the card.
      onContextMenu: (e) => { if (g.current) e.preventDefault(); },
    };
  }

  return { drag, cardProps, cancel: () => finish(false) };
}
